import { StoneColor } from '../types';
/**
 * 围棋规则验证器
 * 负责验证棋步的合法性
 */
export class RuleValidator {
    constructor(board) {
        this.positionHistory = new Set();
        this.board = board;
        this.positionHistory.add(this.board.getHash());
    }
    /**
     * 验证棋步是否合法
     */
    validateMove(coord, color) {
        // PASS是合法的
        if (coord === null) {
            return { isValid: true };
        }
        // 1. 检查坐标是否有效
        if (!this.board.isValidCoordinate(coord)) {
            return {
                isValid: false,
                error: '坐标超出棋盘范围'
            };
        }
        // 2. 检查位置是否已有棋子
        if (this.board.getStone(coord) !== StoneColor.EMPTY) {
            return {
                isValid: false,
                error: '该位置已有棋子'
            };
        }
        // 3. 检查自杀规则
        if (this.isSuicide(coord, color)) {
            return {
                isValid: false,
                error: '自杀着法不允许'
            };
        }
        // 4. 检查劫争规则
        if (this.isKo(coord, color)) {
            return {
                isValid: false,
                error: '劫争未解消'
            };
        }
        // 5. 检查全局同形再现
        if (this.isSuperko(coord, color)) {
            return {
                isValid: false,
                error: '全局同形再现'
            };
        }
        // 计算提子（在落子后的临时棋盘上算气）
        const tempBoard = this.board.clone();
        tempBoard.placeStone(coord, color);
        const capturedStones = this.getCapturedStones(coord, color, tempBoard);
        return {
            isValid: true,
            capturedStones
        };
    }
    /**
     * 执行棋步
     */
    executeMove(coord, color) {
        const validation = this.validateMove(coord, color);
        if (!validation.isValid) {
            throw new Error(validation.error);
        }
        // 如果是PASS
        if (coord === null) {
            return {
                success: true,
                capturedStones: [],
                newBoardState: this.board.getState()
            };
        }
        // 创建临时棋盘用于执行
        const tempBoard = this.board.clone();
        // 放置棋子
        tempBoard.placeStone(coord, color);
        // 提子（必须在已落子的 tempBoard 上计算气）
        const capturedStones = this.getCapturedStones(coord, color, tempBoard);
        for (const capCoord of capturedStones) {
            tempBoard.removeStone(capCoord);
        }
        // 检查是否为劫争
        const isKo = this.checkKo(coord, color, capturedStones);
        // 更新劫争状态
        if (isKo && capturedStones.length === 1) {
            this.koPosition = capturedStones[0];
        }
        else {
            this.koPosition = undefined;
        }
        // 更新历史记录
        this.positionHistory.add(tempBoard.getHash());
        // 回写棋盘状态，让验证器前进到最新局面
        this.board = tempBoard;
        return {
            success: true,
            capturedStones,
            newBoardState: tempBoard.getState(),
            isKo
        };
    }
    /**
     * 检查是否为自杀
     */
    isSuicide(coord, color) {
        // 临时放置棋子
        const tempBoard = this.board.clone();
        tempBoard.placeStone(coord, color);
        // 检查是否提子
        const captured = this.getCapturedStones(coord, color, tempBoard);
        if (captured.length > 0) {
            return false; // 提子了，不是自杀
        }
        // 检查自己是否有气
        const group = tempBoard.findConnectedGroup(coord, color);
        return !tempBoard.hasLiberties(group);
    }
    /**
     * 检查是否为劫争
     */
    isKo(coord, color) {
        if (!this.koPosition)
            return false;
        if (coord.x !== this.koPosition.x || coord.y !== this.koPosition.y)
            return false;
        // 真劫回提只提一子；倒扑/倒脱靴在劫点回提会提掉多子，必须放行
        const tempBoard = this.board.clone();
        tempBoard.placeStone(coord, color);
        const captured = this.getCapturedStones(coord, color, tempBoard);
        return captured.length === 1;
    }
    /**
     * 检查是否为劫争
     * 劫的定义：落子后只提了一个子，且落下的棋块现在只剩一口气，
     * 那口气正好是被提子的位置（对方可立即回提，形成循环）。
     */
    checkKo(coord, color, capturedStones) {
        // 只提了一个子才可能是劫争
        if (capturedStones.length !== 1)
            return false;
        // 创建临时棋盘，落子并提子
        const tempBoard = this.board.clone();
        tempBoard.placeStone(coord, color);
        for (const capCoord of capturedStones) {
            tempBoard.removeStone(capCoord);
        }
        // 落下的棋块只剩一口气，且那口气就是被提的位置
        const group = tempBoard.findConnectedGroup(coord, color);
        const liberties = tempBoard.calculateLiberties(group);
        if (liberties.length !== 1)
            return false;
        const lib = liberties[0];
        const cap = capturedStones[0];
        return lib.x === cap.x && lib.y === cap.y;
    }
    /**
     * 检查全局同形再现
     */
    isSuperko(coord, color) {
        // 创建临时棋盘
        const tempBoard = this.board.clone();
        // 放置棋子
        tempBoard.placeStone(coord, color);
        // 提子
        const capturedStones = this.getCapturedStones(coord, color, tempBoard);
        for (const capCoord of capturedStones) {
            tempBoard.removeStone(capCoord);
        }
        // 检查是否在历史中出现过
        const positionHash = tempBoard.getHash();
        return this.positionHistory.has(positionHash);
    }
    /**
     * 获取提子
     */
    getCapturedStones(coord, color, board) {
        const targetBoard = board || this.board;
        const opponentColor = color === StoneColor.BLACK ? StoneColor.WHITE : StoneColor.BLACK;
        const captured = [];
        // 检查四个方向的对手棋子
        const neighbors = targetBoard.getNeighbors(coord);
        for (const neighbor of neighbors) {
            if (targetBoard.getStone(neighbor) === opponentColor) {
                const group = targetBoard.findConnectedGroup(neighbor, opponentColor);
                if (!targetBoard.hasLiberties(group)) {
                    // 避免重复添加
                    for (const stone of group) {
                        if (!captured.some(c => c.x === stone.x && c.y === stone.y)) {
                            captured.push(stone);
                        }
                    }
                }
            }
        }
        return captured;
    }
    /**
     * 获取上一步的局面哈希
     */
    getPreviousPositionHash() {
        // 获取历史记录中的上一个局面
        const historyArray = Array.from(this.positionHistory);
        return historyArray[historyArray.length - 2] || '';
    }
    /**
     * 重置验证器状态
     */
    reset() {
        this.koPosition = undefined;
        this.positionHistory.clear();
        this.board.clear();
        this.positionHistory.add(this.board.getHash());
    }
    /**
     * 获取对手颜色
     */
    getOpponentColor(color) {
        return color === StoneColor.BLACK ? StoneColor.WHITE : StoneColor.BLACK;
    }
    /**
     * 获取当前棋盘
     */
    getBoard() {
        return this.board;
    }
}
//# sourceMappingURL=RuleValidator.js.map